import React, { useEffect, useState } from 'react'
import NumberFormat from 'react-number-format'
import { moneyString } from '../../../../utils'
import { CancelGroupOrderResponse, Chef } from '@types'
import Loader from '@components/common/Loader'
import { YesNoPill } from './YesNoPill'

interface AdjustChefPayoutsSectionsProps {
  chefs: Chef[]
  cancelGroupOrderResponse: CancelGroupOrderResponse | null
  orderId: string
  isSubmitting: boolean
  onBack: () => void
  onClose: () => void
  onSave: (chefPayoutMap: Record<string, number>) => void
  getEstimatedChefPayouts: (orderId: string) => Promise<Record<string, string>>
}

export const AdjustChefPayoutsSection: React.FC<
  AdjustChefPayoutsSectionsProps
> = ({
  cancelGroupOrderResponse,
  chefs,
  orderId,
  isSubmitting,
  onClose,
  onBack,
  onSave,
  getEstimatedChefPayouts,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [baseChefPayouts, setBaseChefPayouts] = useState<
    Record<string, number>
  >({})
  const [chefPayouts, setChefPayouts] = useState<Record<string, number>>({})

  useEffect(() => {
    const fetchEstimatedBaseChefPayouts = async () => {
      setIsLoading(true)
      const basePayouts = await getEstimatedChefPayouts(orderId)
      const normalized: Record<string, number> = {}
      for (const id in basePayouts) {
        normalized[id] = Number(basePayouts[id])
      }
      setBaseChefPayouts(normalized)
      setIsLoading(false)
    }
    fetchEstimatedBaseChefPayouts()
  }, [getEstimatedChefPayouts, orderId])

  useEffect(() => {
    const next: Record<string, number> = {}
    chefs.forEach((c) => {
      next[c.id] = baseChefPayouts[c.id] ?? 0
    })
    setChefPayouts(next)
  }, [chefs, baseChefPayouts])

  const handleChange = (chefId: string, next: string) => {
    setChefPayouts((prev) => ({
      ...prev,
      [chefId]: Number(next),
    }))
  }

  const setPercent = (chefId: string, pct: number) => {
    const base = baseChefPayouts[chefId] ?? 0
    setChefPayouts((prev) => ({
      ...prev,
      [chefId]: base * pct,
    }))
  }

  const handleSave = () => {
    onSave(chefPayouts)
  }

  if (isLoading) {
    return (
      <div
        className="bg-gray-100 p-8"
        style={{ height: '800px', maxHeight: '800px' }}
      >
        <Loader />
      </div>
    )
  }

  if (cancelGroupOrderResponse) {
    return (
      <CancelGroupOrderResults
        cancelResponse={cancelGroupOrderResponse}
        onClose={onClose}
      />
    )
  }

  return (
    <div className="p-8" style={{ height: '800px', maxHeight: '800px' }}>
      <div
        className="flex flex-col"
        style={{ height: '80%', minHeight: '80%' }}
      >
        <h2 className="font-semibold text-gray-700 mb-8">
          Adjust the payout for {chefs.length === 1 ? 'the chef' : 'each chef'}{' '}
          below:
        </h2>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-10">
          {chefs.map((c, idx) => (
            <div key={c.id}>
              <div className="text-3xl font-medium text-gray-900">{c.name}</div>
              {/* Input */}
              <div className="mt-5 w-full rounded-xl border border-gray-200 bg-white overflow-hidden flex items-center">
                <div className="px-6 py-5 text-3xl text-gray-600 select-none">
                  $
                </div>
                <NumberFormat
                  value={chefPayouts[c.id] ?? ''}
                  onValueChange={(values) => {
                    handleChange(c.id, String(values.floatValue ?? 0))
                  }}
                  decimalScale={2}
                  allowNegative={false}
                  placeholder="0.00"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-5 text-xl outline-none disabled:bg-white"
                />
                <div className="px-8 py-5 text-xl text-gray-900 tabular-nums">
                  ${moneyString(chefPayouts[c.id] ?? '')}
                </div>
              </div>

              {/* percent quick links */}
              <div className="mt-6 flex gap-16 text-xl">
                <button
                  type="button"
                  onClick={() => setPercent(c.id, 0)}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  0%
                </button>
                <button
                  type="button"
                  onClick={() => setPercent(c.id, 0.5)}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  50%
                </button>
                <button
                  type="button"
                  onClick={() => setPercent(c.id, 1)}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:underline disabled:opacity-60"
                >
                  100%
                </button>
              </div>

              {/* divider between chefs */}
              {idx < chefs.length - 1 && (
                <div className="mt-10 border-t border-solid border-gray-300" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-10 flex items-center gap-6">
        {isSubmitting ? (
          <Loader />
        ) : (
          <>
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex-1 h-16 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xl font-semibold transition-colors"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>

            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="flex-1 h-16 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-60 text-gray-900 text-xl font-semibold transition-colors"
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>
  )
}

interface CancelGroupOrderResultsProps {
  cancelResponse: CancelGroupOrderResponse
  onClose: () => void
}

const CancelGroupOrderResults: React.FC<CancelGroupOrderResultsProps> = ({
  cancelResponse,
  onClose,
}) => {
  const hasChefEmailErrors = Object.values(
    cancelResponse.chefEmailErrorMap || {},
  ).some(Boolean)
  const hasChefPayoutErrors = Object.values(
    cancelResponse.chefPayoutErrorMap || {},
  ).some(Boolean)
  const opsEmailSent =
    !cancelResponse.missingOpsEmailDistro &&
    cancelResponse.routingEmailSentError == null

  const hasTopLevelError =
    Boolean(cancelResponse.accountingSyncError) ||
    Boolean(cancelResponse.chefPayoutSyncError) ||
    Boolean(cancelResponse.routingEmailSentError)

  const hasAnyErrors =
    hasTopLevelError ||
    Boolean(cancelResponse.missingOpsEmailDistro) ||
    hasChefEmailErrors ||
    hasChefPayoutErrors
  const hasTechIssue =
    Boolean(cancelResponse.chefPayoutSyncError) ||
    Boolean(cancelResponse.accountingSyncError) ||
    hasChefPayoutErrors
  const hasOpsDistroIssue =
    Boolean(cancelResponse.missingOpsEmailDistro) ||
    Boolean(cancelResponse.routingEmailSentError)

  return (
    <div className="p-8 flex flex-col">
      <div
        className="space-y-8 overflow-y-auto"
        style={{ height: '80%', minHeight: '80%' }}
      >
        <h2 className="text-3xl font-semibold text-gray-900">
          Cancellation Result
        </h2>

        {/* Summary cards */}
        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">Overall</div>
            <div className="mt-3">
              <YesNoPill
                value={!hasAnyErrors}
                trueLabel="Success"
                falseLabel="Completed w/ errors"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Group Order Updated
            </div>
            <div className="mt-3">
              <YesNoPill
                value={cancelResponse.groupOrderUpdated}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Route Updated
            </div>
            <div className="mt-3">
              <YesNoPill
                value={cancelResponse.routeUpdated}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Date Menus Updated
            </div>
            <div className="mt-3">
              <YesNoPill
                value={cancelResponse.dateMenusUpdated}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
          </div>

          {opsEmailSent ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="text-sm font-semibold text-gray-600">
                Operations Email Sent
              </div>
              <div className="mt-3">
                <YesNoPill
                  value={opsEmailSent}
                  trueLabel="Yes"
                  falseLabel="No"
                />
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="text-sm font-semibold text-gray-600">
                Missing Ops Email Distro
              </div>
              <div className="mt-3">
                <YesNoPill
                  value={cancelResponse.missingOpsEmailDistro}
                  trueLabel="Yes"
                  falseLabel="No"
                />
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Chef Payouts Synced
            </div>
            <div className="mt-3">
              <YesNoPill
                value={!cancelResponse.chefPayoutSyncError}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Accounting Synced
            </div>
            <div className="mt-3">
              <YesNoPill
                value={!cancelResponse.accountingSyncError}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
          </div>
        </div>
        {/* Next Steps */}
        {(hasOpsDistroIssue || hasTechIssue || hasChefEmailErrors) && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="text-sm font-semibold text-gray-600">
              Next steps
            </div>

            <ul className="mt-4 space-y-2 text-sm text-gray-700 list-disc pl-5">
              {hasChefEmailErrors && (
                <li>
                  Some{' '}
                  <span className="font-semibold">
                    chef cancellation emails
                  </span>{' '}
                  failed to send. Please notify the affected chefs directly.
                </li>
              )}

              {cancelResponse.missingOpsEmailDistro && (
                <li>
                  <span className="font-semibold">
                    Ops email distro is missing
                  </span>
                  . Please reach out to the Ops team alert them the order was
                  cancelled.
                </li>
              )}

              {cancelResponse.routingEmailSentError && (
                <li>
                  <span className="font-semibold">
                    Ops notification email failed
                  </span>
                  . Please alert Ops manually and notify the Tech Team to
                  investigate.
                </li>
              )}

              {hasTechIssue && (
                <li>
                  There were{' '}
                  <span className="font-semibold">payout/accounting sync</span>{' '}
                  issues. Please contact the Tech Team to review and re-sync.
                </li>
              )}
            </ul>
          </div>
        )}

        {hasTopLevelError && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            {cancelResponse.accountingSyncError && (
              <div className="mt-5 rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 break-words">
                <div className="font-semibold">Accounting Sync Error</div>
                <div className="mt-1">{cancelResponse.accountingSyncError}</div>
              </div>
            )}
            {cancelResponse.chefPayoutSyncError && (
              <div className="mt-5 rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 break-words">
                <div className="font-semibold">Chef Payout Sync Error</div>
                <div className="mt-1">{cancelResponse.chefPayoutSyncError}</div>
              </div>
            )}
            {cancelResponse.routingEmailSentError && (
              <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 break-words">
                <div className="font-semibold">Routing Email Error</div>
                <div className="mt-1">
                  {cancelResponse.routingEmailSentError}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-600">
              Chef Cancellation Email Results
            </div>
            <YesNoPill
              value={!hasChefEmailErrors}
              trueLabel="All Sent"
              falseLabel="Some Failed"
            />
          </div>

          {Object.keys(cancelResponse.chefEmailErrorMap || {}).length === 0 ? (
            <div className="mt-4 text-sm text-gray-500">
              No chef email results returned.
            </div>
          ) : (
            <div className="mt-4 max-h-[280px] overflow-y-auto">
              <table className="min-w-full w-full table-fixed">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <th className="py-3 pr-4 w-[45%]">Chef</th>
                    <th className="py-3 pr-4 w-[20%] text-center">
                      Email Sent
                    </th>
                    <th className="py-3 w-[35%]">Error</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {Object.entries(cancelResponse.chefEmailErrorMap).map(
                    ([chefEmail, err]) => {
                      const sent = err == null

                      return (
                        <tr key={chefEmail} className="text-sm text-gray-800">
                          <td className="py-4 pr-4 font-medium text-gray-900 break-words">
                            {chefEmail}
                          </td>
                          <td className="py-4 pr-4 text-center">
                            <YesNoPill
                              value={sent}
                              trueLabel="Yes"
                              falseLabel="No"
                            />
                          </td>
                          <td className="py-4">
                            {sent ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className="text-red-700 break-words">
                                {err}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    },
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-600">
              Chef Payout Update Results
            </div>
            <YesNoPill
              value={!hasChefPayoutErrors}
              trueLabel="All Updated"
              falseLabel="Some Failed"
            />
          </div>

          {Object.keys(cancelResponse.chefPayoutErrorMap || {}).length === 0 ? (
            <div className="mt-4 text-sm text-gray-500">
              No chef payout results returned.
            </div>
          ) : (
            <div className="mt-4 max-h-[280px] overflow-y-auto">
              <table className="min-w-full w-full table-fixed">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-sm font-semibold text-gray-700 border-b border-gray-200">
                    <th className="py-3 pr-4 w-[45%]">Chef</th>
                    <th className="py-3 pr-4 w-[20%] text-center">Updated</th>
                    <th className="py-3 w-[35%]">Error</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {Object.entries(cancelResponse.chefPayoutErrorMap).map(
                    ([chefEmail, err]) => {
                      const updated = err == null

                      return (
                        <tr key={chefEmail} className="text-sm text-gray-800">
                          <td className="py-4 pr-4 font-medium text-gray-900 break-words">
                            {chefEmail}
                          </td>
                          <td className="py-4 pr-4 text-center">
                            <YesNoPill
                              value={updated}
                              trueLabel="Yes"
                              falseLabel="No"
                            />
                          </td>
                          <td className="py-4">
                            {updated ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className="text-red-700 break-words">
                                {err}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    },
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-10 border-t border-solid border-gray-200 flex items-center gap-6">
        <button
          onClick={onClose}
          className="flex-1 h-16 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xl font-semibold transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  )
}
